import { useEffect, useRef, useState, forwardRef, useImperativeHandle, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from 'prop-types';
import "../App.css";
import { IoIosArrowDroprightCircle, IoIosSend } from "react-icons/io";
import { FaQuestionCircle, FaUserNurse, FaUserPlus, FaStop } from "react-icons/fa";
import { AiFillRobot } from "react-icons/ai";
import { RiMicAiFill } from "react-icons/ri";
import { IoMdClose } from "react-icons/io";
import AudioVisualizer from "./AudioVisualizer";
import SpeechToTextService from "../services/SpeechToTextService";
import { InputBase } from "@mui/material";
import TenantContext from '../Context/TenantContext';
import './LexChatbot.css';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api/v1';

const getAuthToken = () => localStorage.getItem('token') || sessionStorage.getItem('token');

const cleanMessageContent = (content) => {
  if (!content) return content;
  let cleaned = content.toString().trim();
  if (cleaned.startsWith('"') && cleaned.endsWith('"')) cleaned = cleaned.slice(1, -1);
  if (cleaned.startsWith("'") && cleaned.endsWith("'")) cleaned = cleaned.slice(1, -1);
  return cleaned;
};

const convertLinksToAnchors = (text) => {
  if (!text) return text;
  const urlRegex = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;
  return text.split(/(\s+)/).map((part, index) => {
    if (part.match(urlRegex)) {
      const displayText = part.replace(/^https?:\/\//, '');
      return (
        <a key={index} href={part} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-blue-400 hover:text-blue-300 underline">
          {displayText}
        </a>
      );
    }
    return part;
  });
};

const LexChatbot = forwardRef(({ onToggle, userData: _userData, onSpeakingChange, autoFocusInput = true }, ref) => {
  // Get tenant context
  const { tenant = {} } = useContext(TenantContext) || {};
  const navigate = useNavigate();
  
  // Core state
  const [messages, setMessages] = useState([]);
  const [conversationStatus] = useState("");
  const [sessionId] = useState(`user-${Date.now()}`);
  const [sessionAttributes, setSessionAttributes] = useState({});

  // Voice/speech state
  const [recording, setRecording] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [textMode, setTextMode] = useState(false);

  // UI/UX
  const chatListRef = useRef(null);
  const [textInputValue, setTextInput] = useState("");
  const inputRef = useRef(null);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 800);
  const [bottomOffset, setBottomOffset] = useState(0);
  const inputBarRef = useRef(null);
  const [inputBarHeight, setInputBarHeight] = useState(72);

  // Audio refs
  const autoListenTimeout = useRef(null);
  const currentPlayingAudio = useRef(null);
  const latestMessageId = useRef(null);
  const audioVisualizerRefs = useRef({});
  const lastStopTime = useRef(0);
  const restartAttempts = useRef(0);
  const safariAudioUrls = useRef(new Set());

  // Services
  const [speechToTextService] = useState(() => new SpeechToTextService());

  // Hands-free mode flag
  const autoListenEnabled = true;

  // Helpers
  const scrollToBottom = useCallback(() => {
    try {
      if (chatListRef.current) {
        const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
        const delay = isMobile ? (isKeyboardVisible ? 180 : 120) : 0;
        setTimeout(() => {
          if (chatListRef.current) {
            const sc = chatListRef.current;
            const nearBottom = (sc.scrollHeight - sc.scrollTop - sc.clientHeight) < 120;
            if (nearBottom || messages.length === 0) {
              sc.scrollTop = sc.scrollHeight;
              if (!isMobile || messages.length === 0) sc.scrollTo({ top: sc.scrollHeight, behavior: 'smooth' });
            }
          }
        }, delay);
      }
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }, [isKeyboardVisible, messages.length]);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768 && messages.length > 0) {
      const id = setTimeout(() => scrollToBottom(), 100);
      return () => clearTimeout(id);
    }
  }, [messages.length, scrollToBottom]);
  useEffect(() => { if (inputRef.current && autoFocusInput) inputRef.current.focus(); }, [autoFocusInput]);

  // Init and cleanup
  useEffect(() => {
    setSpeechSupported(speechToTextService.isSupported());
    speechToTextService.setHandsFreeMode(true);
    const currentUrls = safariAudioUrls.current;
    return () => {
      speechToTextService.cleanup();
  if (autoListenTimeout.current) { try { clearTimeout(autoListenTimeout.current); } catch (e) { /* no-op */ }
      }
  currentUrls.forEach(url => { try { URL.revokeObjectURL(url); } catch (e) { /* no-op */ } });
      currentUrls.clear();
    };
  }, [speechToTextService]);

  // Aggregate state for timers
  const stateRef = useRef({ recording: false, isRecognizing: false, isListening: false, isSpeaking: false, autoListenEnabled: true, speechSupported: false, textMode: false });
  useEffect(() => { stateRef.current = { recording, isRecognizing, isListening, isSpeaking, autoListenEnabled: true, speechSupported, textMode }; }, [recording, isRecognizing, isListening, isSpeaking, speechSupported, textMode]);

  // Mobile viewport and keyboard handling
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return;
    const computeBottomOffset = () => {
      try {
        if (window.visualViewport) {
          const vv = window.visualViewport;
          // Keyboard height approximation: layout height - (visual height + offsetTop)
          const keyboardHeight = Math.max(window.innerHeight - (vv.height + vv.offsetTop), 0);
          return Math.round(keyboardHeight);
        }
      } catch (e) { /* no-op */ }
      return 0;
    };
    const getActualViewportHeight = () => {
      if (window.visualViewport) return window.visualViewport.height;
      if (/iPhone|iPad|iPod/.test(navigator.userAgent)) return Math.min(window.innerHeight, window.screen.height - 100);
      if (/Android/.test(navigator.userAgent)) return Math.min(window.innerHeight, window.screen.height - 100);
      return window.innerHeight;
    };
    const initialHeight = getActualViewportHeight();
    setViewportHeight(initialHeight);
    setBottomOffset(computeBottomOffset());
    const measureInputBar = () => {
      try { if (inputBarRef.current) { const r = inputBarRef.current.getBoundingClientRect(); setInputBarHeight(Math.max(56, Math.round(r.height))); } } catch (e) { /* no-op */ }
    };
    measureInputBar();
  const handleViewportChange = () => {
      const currentViewportHeight = getActualViewportHeight();
      const isKeyboardOpen = currentViewportHeight < initialHeight - 150;
      setIsKeyboardVisible(isKeyboardOpen);
      setViewportHeight(currentViewportHeight);
      setBottomOffset(computeBottomOffset());
      measureInputBar();
    };
    const handleResize = () => { setViewportHeight(getActualViewportHeight()); measureInputBar(); };
    if (window.visualViewport) window.visualViewport.addEventListener('resize', handleViewportChange);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      if (window.visualViewport) window.visualViewport.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isMobile = window.innerWidth <= 768;
    const el = inputRef.current;
    if (!isMobile || !el) return;
    const handleFocus = () => {
      setIsKeyboardVisible(true);
      // Avoid scrollIntoView to prevent jump-to-top on some mobile browsers
      setTimeout(() => { try { /* keep position */ } catch (e) { /* no-op */ } scrollToBottom(); }, 80);
    };
    const handleBlur = () => setTimeout(() => setIsKeyboardVisible(false), 150);
    el.addEventListener('focus', handleFocus);
    el.addEventListener('blur', handleBlur);
    return () => { el.removeEventListener('focus', handleFocus); el.removeEventListener('blur', handleBlur); };
  }, [scrollToBottom]);

  // Methods exposed to parent
  useImperativeHandle(ref, () => ({ startListening: () => { startRecordingWeb(); }, stopListening: () => { stopRecordingWeb(); } }));

  // Messaging
  const sendMessage = async (message) => {
    if (message && message.length > 0) {
      try {
        const cleanedMessage = cleanMessageContent(message);
        const userMessageId = Date.now();
        setMessages(prev => [...prev, { content: cleanedMessage, sender: 'user', contentType: 'PlainText', key: userMessageId }]);
        const typingIndicatorId = userMessageId + 1;
        setMessages(prev => [...prev, { content: 'Typing...', sender: 'bot', contentType: 'PlainText', key: typingIndicatorId, isTyping: true }]);
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/lex/text`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ message: cleanedMessage, sessionId, sessionAttributes }) });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setMessages(prev => prev.filter(msg => msg.key !== typingIndicatorId));
        const responseData = data.data || data;
        const msgs = responseData.messages || [];
        const firstMessage = msgs[0];
        
        // Extract message content
        const messageContent = firstMessage?.content || "I didn't receive a response. Please try again.";
        
        // Create audio URL from base64 data if available
        let audioUrl = null;
        if (responseData.audioBuffer) {
          // Validate the base64 audio data
          try {
            const audioData = atob(responseData.audioBuffer);
            console.log('Audio buffer length:', responseData.audioBuffer.length);
            console.log('Decoded audio data length:', audioData.length);
            
            // Basic validation - audio files should be reasonably sized
            if (audioData.length < 100) {
              console.error('Audio data seems too small, might be corrupted');
            }
          } catch (error) {
            console.error('Invalid base64 audio data:', error);
            return; // Skip audio creation if data is invalid
          }
          
          // Check if we're on Safari and adjust audio format accordingly
          const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
          
          if (isSafari) {
            // For Safari, try to create a blob URL which is more reliable than data URLs
            try {
              const audioData = atob(responseData.audioBuffer);
              const arrayBuffer = new ArrayBuffer(audioData.length);
              const uint8Array = new Uint8Array(arrayBuffer);
              
              for (let i = 0; i < audioData.length; i++) {
                uint8Array[i] = audioData.charCodeAt(i);
              }
              
              // Try multiple MIME types that Safari supports
              const supportedTypes = [
                'audio/mp4', // Safari's preferred format
                'audio/mpeg', // MP3
                'audio/wav', // WAV
                'audio/aac', // AAC
                'audio/x-m4a' // M4A
              ];
              
              let blob = null;
              for (const mimeType of supportedTypes) {
                // Create a test audio element to check if the format is supported
                const testAudio = document.createElement('audio');
                if (testAudio.canPlayType(mimeType)) {
                  blob = new Blob([arrayBuffer], { type: mimeType });
                  console.log(`Using Safari-compatible format: ${mimeType}`);
                  break;
                }
              }
              
              // Fallback to MP4 if no specific format is detected as supported
              if (!blob) {
                blob = new Blob([arrayBuffer], { type: 'audio/mp4' });
                console.log('Using fallback format: audio/mp4');
              }
              
              audioUrl = URL.createObjectURL(blob);
              console.log('Created Safari-compatible blob URL:', audioUrl);
              
              // Track the URL for cleanup
              safariAudioUrls.current.add(audioUrl);
            } catch (error) {
              console.error('Error creating Safari blob URL:', error);
              // Fallback to data URL
              audioUrl = `data:audio/mpeg;base64,${responseData.audioBuffer}`;
            }
          } else {
            // For other browsers, use data URL as before
            audioUrl = `data:audio/mpeg;base64,${responseData.audioBuffer}`;
          }
        }
        
        // Add bot response
        const botMessageId = Date.now() + 2;
        
        // Set this as the latest message
        latestMessageId.current = botMessageId;
        
        // Stop any currently playing audio to prevent overlap
        if (currentPlayingAudio.current) {
          currentPlayingAudio.current.pause();
          currentPlayingAudio.current = null;
        }
        
        // Stop all existing audio visualizers
        Object.values(audioVisualizerRefs.current).forEach(ref => {
          if (ref && ref.stop) {
            ref.stop();
          }
        });
        
        // Reset speaking state
        if (isSpeaking) {
          setIsSpeaking(false);
          if (onSpeakingChange) onSpeakingChange(false);
        }
        const botMessage = {
          content: messageContent,
          sender: "bot",
          contentType: "PlainText",
          key: botMessageId,
          audioStream: audioUrl,
          audioBytes: responseData.audioBuffer,
          autoPlay: !textMode,
          card: firstMessage?.imageResponseCard || null,
          isLatest: true // Mark as the latest message
        };

        setMessages((prevMessages) => {
          // Mark all previous messages as not latest
          const updatedMessages = prevMessages.map(msg => ({
            ...msg,
            isLatest: false
          }));
          return [...updatedMessages, botMessage];
        });

        // Update session attributes if provided
        if (responseData.sessionAttributes) {
          setSessionAttributes(responseData.sessionAttributes);
        }

        // Handle voice response
        if (audioUrl && !textMode) {
          const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
          
          if (isSafari) {
            // For Safari, the AudioVisualizer will handle the user gesture requirement
            console.log('Safari detected - AudioVisualizer will require user gesture for audio playback');
          }
          
          setIsSpeaking(true);
          if (onSpeakingChange) onSpeakingChange(true);
        }

        // Force scroll to bottom after adding message
        setTimeout(() => {
          scrollToBottom();
        }, 100);

        // Auto-start listening mode after response (optimized for hands-free experience)
        if (!textMode && speechSupported && autoListenEnabled) {
          // Clear any existing timeout
          if (autoListenTimeout.current) {
            clearTimeout(autoListenTimeout.current);
          }
          
          // Optimized delay: shorter for text-only responses, longer for audio responses
          const delay = audioUrl ? 2500 : 800; // Reduced delays for faster response
          
          autoListenTimeout.current = setTimeout(() => {
            // Use ref to get the latest state values (avoiding stale closure)
            const currentStates = stateRef.current;
            console.log('🎙️ Auto-listen attempt after response:', currentStates);
            
            // More aggressive startup for hands-free mode
            if (currentStates.autoListenEnabled && currentStates.speechSupported && !currentStates.textMode) {
              console.log('🚀 Fast-starting listening after response (hands-free mode)...');
              
              // Force reset all speech-related states
              setIsSpeaking(false);
              setIsRecognizing(false);
              setIsListening(false);
              setRecording(false);
              
              // Immediate startup for better conversational flow
              setTimeout(() => {
                speechToTextService.setHandsFreeMode(true);
                startRecordingWeb();
              }, 50);
            } else {
              console.log('Not starting listening after response. States:', currentStates);
            }
            
            autoListenTimeout.current = null;
          }, delay);
        }

      } catch (error) {
        console.error('Error sending message:', error);
        
        // Remove typing indicator on error
        setMessages((prevMessages) => 
          prevMessages.filter(msg => !msg.isTyping)
        );

        // Add error message
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            content: "Sorry, I'm having trouble connecting. Please try again.",
            sender: "bot",
            contentType: "PlainText",
            key: Date.now(),
            isError: true
          }
        ]);
      }
    }
  };

  // Start speech-to-text function (optimized for hands-free conversational experience)
  const startRecordingWeb = async () => {
    try {
      if (!speechSupported) {
        console.error('Speech recognition is not supported in this browser');
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            content: "Speech recognition is not supported in this browser. Please try typing your message instead.",
            sender: "bot",
            contentType: "PlainText",
            key: Date.now(),
            isError: true
          }
        ]);
        return;
      }

      // Check if we're on mobile and handle accordingly
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      if (isMobile) {
        console.log('🎙️ Starting speech recognition on mobile device...');
      }

      console.log('🎙️ Starting optimized speech recognition for hands-free experience...');
      
      // Force reset all states before starting - optimized for quick turnaround
      setTextMode(false); // Voice mode
      setRecording(false);
      setIsRecognizing(false);
      setIsListening(false);
      setIsSpeaking(false);
      
      // Minimal delay for faster startup in hands-free mode
      await new Promise(resolve => setTimeout(resolve, isMobile ? 100 : 25));
      
      // Now set the active states
      setRecording(true);
      setIsRecognizing(true);
      setIsListening(true);

      const started = speechToTextService.startListening({
        onStart: () => {
          console.log('🎙️ Speech recognition started - ready for hands-free conversation');
          setIsRecognizing(true);
          setIsListening(true);
        },
        onResult: (result) => {
          console.log('🎤 Speech result:', result);
          
          // Show the recognized text in the input field immediately
          setTextInput(result.interim);
          
          // If we have a final result, send it as a message
          if (result.isFinal && result.final.trim()) {
            console.log('✅ Final speech result:', result.final);
            const finalText = result.final.trim();
            setTextInput(finalText);
            
            // Check for close commands
            const closeKeywords = ['bye', 'exit', 'close', 'goodbye', 'see you later', 'talk to you later'];
            const isCloseCommand = closeKeywords.some(keyword => 
              finalText.toLowerCase().includes(keyword.toLowerCase())
            );
            
            if (isCloseCommand && onToggle) {
              console.log('🔄 Close command detected, minimizing chat window...');
              // Stop all activity first
              setIsRecognizing(false);
              setIsListening(false);
              setRecording(false);
              // Trigger close after a brief delay to allow user to see the message
              setTimeout(() => {
                onToggle();
              }, 1500);
              return;
            }
            
            // In hands-free mode, prepare for quick restart after response
            if (autoListenEnabled) {
              console.log('🔄 Hands-free mode: Preparing for quick restart after response');
              // Reset restart attempts on successful speech recognition
              restartAttempts.current = 0;
              // Don't immediately stop - let the response handling manage the flow
              setIsRecognizing(false);
              setIsListening(false);
              setRecording(false);
            } else {
              setIsRecognizing(false);
              setIsListening(false);
              setRecording(false);
            }
            
            // Send the recognized text as a message
            sendMessage(finalText);
            setTextInput(''); // Clear after sending
          }
        },
        onError: (error) => {
          console.error('🚫 Speech recognition error:', error);
          setIsRecognizing(false);
          setIsListening(false);
          setRecording(false);
          setTextInput(''); // Clear input on error
          
          // Don't show error for common cases that are part of normal operation
          const isNormalError = error.message.includes('No speech was detected') || 
                               error.message.includes('aborted') ||
                               error.message.includes('not-allowed') ||
                               error.message.includes('audio-capture');
          
          // Only show error messages for unexpected errors
          if (!isNormalError) {
            setMessages((prevMessages) => [
              ...prevMessages,
              {
                content: `Speech recognition error: ${error.message}`,
                sender: "bot",
                contentType: "PlainText",
                key: Date.now(),
                isError: true
              }
            ]);
          }
          
          // In hands-free mode, only restart if it's not a user-initiated abort or permission issue
          if (autoListenEnabled && speechSupported && !isNormalError) {
            // Implement debounce to prevent rapid restart loops
            const now = Date.now();
            const timeSinceLastStop = now - lastStopTime.current;
            
            // Only restart if enough time has passed and we haven't attempted too many restarts
            if (timeSinceLastStop > 3000 && restartAttempts.current < 3) {
              restartAttempts.current++;
              setTimeout(() => {
                // Double check if we should still restart (user might have disabled hands-free mode)
                if (stateRef.current.autoListenEnabled && !stateRef.current.recording && !stateRef.current.isRecognizing) {
                  console.log(`🔄 Restarting listening after speech error (attempt ${restartAttempts.current}/3)`);
                  startRecordingWeb();
                }
              }, 2000 * restartAttempts.current); // Exponential backoff
            } else {
              console.log('🚫 Skipping restart due to debounce or too many attempts');
              if (restartAttempts.current >= 3) {
                console.log('🚫 Maximum restart attempts reached, disabling auto-restart temporarily');
                // Reset after a longer delay
                setTimeout(() => {
                  restartAttempts.current = 0;
                }, 30000);
              }
            }
          }
        },
        onEnd: () => {
          console.log('🛑 Speech recognition ended');
          setIsRecognizing(false);
          setIsListening(false);
          setRecording(false);
          
          // If we have text in the input field, send it
          if (textInputValue.trim()) {
            sendMessage(textInputValue.trim());
            setTextInput('');
          }
          
          // In hands-free mode, the auto-listen logic will handle immediate restart
        }
      }, autoListenEnabled); // Pass hands-free mode flag

      if (!started) {
        console.error('❌ Failed to start speech recognition');
        setRecording(false);
        setIsRecognizing(false);
        setIsListening(false);
        
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            content: "Failed to start speech recognition. Please check your microphone permissions and try again.",
            sender: "bot",
            contentType: "PlainText",
            key: Date.now(),
            isError: true
          }
        ]);
      }
      
    } catch (error) {
      console.error('❌ Error starting speech recognition:', error);
      setRecording(false);
      setIsRecognizing(false);
      setIsListening(false);
      
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          content: "Error starting speech recognition. Please try again.",
          sender: "bot",
          contentType: "PlainText",
          key: Date.now(),
          isError: true
        }
      ]);
    }
  };

  // Stop speech recognition
  const stopRecordingWeb = () => {
    try {
      console.log('🛑 Manually stopping speech recognition...');
      
      // Update last stop time for debouncing
      lastStopTime.current = Date.now();
      
      // Clear any pending auto-listen timeouts to prevent restart
      if (autoListenTimeout.current) {
        clearTimeout(autoListenTimeout.current);
        autoListenTimeout.current = null;
      }
      
      // Force disable hands-free mode temporarily to prevent auto-restart
      speechToTextService.setHandsFreeMode(false);
      
      // Set states first to prevent race conditions
      setIsRecognizing(false);
      setIsListening(false);
      setRecording(false);
      
      // Stop the speech recognition service
      speechToTextService.stopListening();
      
      // If we have text in the input field, send it
      if (textInputValue.trim()) {
        sendMessage(textInputValue.trim());
        setTextInput('');
      }
      
      // Re-enable hands-free mode after a short delay if it was enabled
      if (autoListenEnabled) {
        setTimeout(() => {
          speechToTextService.setHandsFreeMode(autoListenEnabled);
        }, 1000);
      }
    } catch (error) {
      console.error('Error stopping speech recognition:', error);
      // Force reset states even if stopping failed
      setIsRecognizing(false);
      setIsListening(false);
      setRecording(false);
    }
  };

  // Audio lifecycle hooks for visualizer
  const handleAudioPlay = (messageId) => {
    if (messageId && latestMessageId.current !== messageId) return;
    if (currentPlayingAudio.current) { currentPlayingAudio.current.pause(); currentPlayingAudio.current = null; }
    setIsSpeaking(true); if (onSpeakingChange) onSpeakingChange(true);
  };

  const handleAudioPause = (messageId) => {
    if (messageId && latestMessageId.current !== messageId) return;
    setIsSpeaking(false); setIsRecognizing(false); setIsListening(false); setRecording(false);
    if (onSpeakingChange) onSpeakingChange(false);
    if (currentPlayingAudio.current) { currentPlayingAudio.current = null; }
    if (speechSupported) {
      if (autoListenTimeout.current) { clearTimeout(autoListenTimeout.current); }
      autoListenTimeout.current = setTimeout(() => {
        setIsSpeaking(false); setIsRecognizing(false); setIsListening(false); setRecording(false);
        setTimeout(() => { speechToTextService.setHandsFreeMode(true); startRecordingWeb(); }, 50);
        autoListenTimeout.current = null;
      }, 200);
    }
  };

  const stopAllActivity = () => {
    try {
      lastStopTime.current = Date.now(); restartAttempts.current = 0;
      if (autoListenTimeout.current) { clearTimeout(autoListenTimeout.current); autoListenTimeout.current = null; }
      if (isRecognizing || recording || isListening) {
        speechToTextService.setHandsFreeMode(false);
        speechToTextService.abort();
        setIsRecognizing(false); setIsListening(false); setRecording(false);
      }
      if (isSpeaking) {
        setIsSpeaking(false); if (onSpeakingChange) onSpeakingChange(false);
        if (currentPlayingAudio.current) { currentPlayingAudio.current.pause(); currentPlayingAudio.current = null; }
        Object.values(audioVisualizerRefs.current).forEach(ref => { if (ref && ref.stop) ref.stop(); });
      }
      setTextInput('');
      setTimeout(() => { speechToTextService.setHandsFreeMode(true); }, 2000);
    } catch (error) {
      setIsRecognizing(false); setIsListening(false); setRecording(false); setIsSpeaking(false); if (onSpeakingChange) onSpeakingChange(false);
    }
  };

  const openAIAssistant = (message = '') => {
    const initialInput = message.trim();

    try {
      lastStopTime.current = Date.now();
      restartAttempts.current = 0;
      if (autoListenTimeout.current) {
        clearTimeout(autoListenTimeout.current);
        autoListenTimeout.current = null;
      }
      speechToTextService.setHandsFreeMode(false);
      speechToTextService.abort();
      if (currentPlayingAudio.current) {
        currentPlayingAudio.current.pause();
        currentPlayingAudio.current = null;
      }
      Object.values(audioVisualizerRefs.current).forEach(ref => { if (ref && ref.stop) ref.stop(); });
    } catch (error) {
      console.warn('Could not stop Lex activity before opening AI Assistant:', error);
    }

    setIsRecognizing(false);
    setIsListening(false);
    setRecording(false);
    setIsSpeaking(false);
    if (onSpeakingChange) onSpeakingChange(false);
    setTextInput('');
    if (onToggle) onToggle();

    if (initialInput) {
      navigate('/ai-assistant', { state: { initialInput } });
    } else {
      navigate('/ai-assistant');
    }
  };

  const sendCommand = (command) => { sendMessage(command); };
  const startProcedure = (procedure) => { sendMessage(procedure); };

  // UI
  const isMobile = typeof window !== 'undefined' ? window.innerWidth <= 768 : false;

  const containerClassName = [
    'chatgpt-shell',
    'flex',
    'flex-col',
    'h-full',
    'overflow-hidden'
  ];

  if (isMobile) {
    containerClassName.push('mobile-chat-fix');
    if (isKeyboardVisible) {
      containerClassName.push('mobile-keyboard-open');
    }
  }

  const containerStyle = isMobile
    ? {
        height: `${Math.max(viewportHeight, 320)}px`,
        maxHeight: `${Math.max(viewportHeight, 320)}px`
      }
    : undefined;
  
  return (
    <div
      className={containerClassName.join(' ')}
      style={containerStyle}
    >
      {/* Header */}
      <div className="chatgpt-header">
        <div className="chatgpt-header-left">
          <div className={`chatgpt-avatar ${isSpeaking ? 'chatgpt-avatar-speaking' : ''}`}>
            <img src="./imgs/robot.png" alt="AI Avatar" className="w-6 h-6" />
          </div>
          <div>
            <p className="chatgpt-title">FLO</p>
            <p className="chatgpt-subtitle">
              {isSpeaking ? 'Speaking' : isListening ? 'Listening' : isRecognizing ? 'Processing' : recording ? 'Recording' : 'Ready'}
            </p>
          </div>
        </div>
        {onToggle && (
          <button onClick={onToggle} className="chatgpt-close-btn" aria-label="Close chat">
            <IoMdClose className="text-lg" />
          </button>
        )}
      </div>

      {/* Main */}
      <div className="chatgpt-body bg-white flex-1 flex flex-col min-h-0">
        {messages.length === 0 ? (
          <div className="chatgpt-empty-state">
            <div className="chatgpt-quick-actions">
              {[
                { label: 'AI Agent', hint: 'Kannada', action: () => { if (onToggle) onToggle(); navigate('/ai-assistant'); }, icon: <AiFillRobot /> },
                { label: `${tenant?.name || 'Smart Nurse'} Agent`, hint: `Open the ${tenant?.name || 'Smart Nurse'} Agent`, action: () => { if (onToggle) onToggle(); navigate('/ai-agent'); }, icon: <FaUserNurse /> },
                { label: 'FAQs', hint: 'Jump to FAQs', action: () => { if (onToggle) onToggle(); navigate('/ai-agent'); }, icon: <FaQuestionCircle /> },
                { label: 'Patient Registration', hint: 'Register as a new patient', action: () => { if (onToggle) onToggle(); navigate('/patient-registration'); }, icon: <FaUserPlus /> }
              ].map((item, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={item.action}
                  className="chatgpt-quick-action-btn"
                >
                  <span className="chatgpt-quick-action-icon" aria-hidden="true">{item.icon}</span>
                  <span className="chatgpt-quick-action-meta">
                    <span className="chatgpt-quick-action-label">{item.label}</span>
                    <span className="chatgpt-quick-action-hint">{item.hint}</span>
                  </span>
                  <IoIosArrowDroprightCircle className="chatgpt-quick-action-arrow" aria-hidden="true" />
                </button>
              ))}
            </div>

            {!speechSupported && (
              <p className="chatgpt-helper-text">Speech recognition isn't supported in this browser. Try typing your questions instead.</p>
            )}
          </div>
        ) : (
          <div
            ref={chatListRef}
            className="chatgpt-thread"
          >
            <div className="chatgpt-thread-inner">
              {messages.map((message, index) => (
                <div key={`${index}-${message.key || ''}`} className={`chatgpt-message ${message.sender === 'user' ? 'chatgpt-message-user' : 'chatgpt-message-bot'} ${message.isError ? 'chatgpt-message-error' : ''}`}>
                  <div className="chatgpt-bubble">
                    <div className={`chatgpt-bubble-content ${message.isTyping ? 'chatgpt-bubble-typing' : ''}`}>
                      {message.isTyping ? (
                        <>
                          <span className="sr-only">FLO is typing</span>
                          <span className="chatgpt-typing-dot" aria-hidden="true"></span>
                        </>
                      ) : (
                        <div className="whitespace-pre-wrap leading-relaxed">
                          {convertLinksToAnchors(cleanMessageContent(message.content))}
                        </div>
                      )}
                    </div>
                    {message.audioStream && (
                      <div className="chatgpt-audio">
                        <AudioVisualizer
                          key={`audio-${index}-${message.key || Date.now()}`}
                          ref={(ref) => { if (ref) { audioVisualizerRefs.current[`audio-${index}-${message.key || Date.now()}`] = ref; } }}
                          audioUrl={message.audioStream}
                          audioBytes={message.audioBytes}
                          autoplay={message.autoPlay && message.isLatest}
                          onPlay={() => handleAudioPlay(message.key)}
                          onPause={() => handleAudioPause(message.key)}
                          onAudioEnd={message.onAudioEnd}
                          disabled={!message.isLatest}
                        />
                      </div>
                    )}
                    {message.card && (
                      <div className="chatgpt-card">
                        {message.card.imageUrl && (<img src={message.card.imageUrl} alt={message.card.title} className="chatgpt-card-image" />)}
                        <div className="chatgpt-card-body">
                          {message.card.title && (<h3 className="chatgpt-card-title">{message.card.title}</h3>)}
                          {message.card.subtitle && (<p className="chatgpt-card-subtitle">{message.card.subtitle}</p>)}
                          {message.card.buttons && message.card.buttons.length > 0 && (
                            <div className="chatgpt-card-actions">
                              {message.card.buttons.map((button, idx) => (
                                <button key={idx} onClick={() => sendCommand(button.value)} className="chatgpt-card-button">{button.text}</button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="chatgpt-footer">
          <div className="chatgpt-input-row">
            <div className={`chatgpt-input-field ${isRecognizing ? 'chatgpt-input-field-active' : ''}`}>
              <InputBase
                inputRef={inputRef}
                fullWidth
                type="text"
                placeholder={isRecognizing ? 'Listening…' : 'Message FLO'}
                value={textInputValue}
                onChange={(e) => {
                  setTextInput(e.target.value);
                }}
                onFocus={() => {
                  if (isMobile && inputRef.current) {
                    requestAnimationFrame(() => {
                      inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    });
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (textInputValue.trim()) {
                      openAIAssistant(textInputValue);
                    }
                  }
                }}
                className="chatgpt-input"
                sx={{ '& .MuiInputBase-input': { color: '#111827', fontSize: isMobile ? '16px' : '15px', fontWeight: 500, '&::placeholder': { color: isRecognizing ? '#2563eb' : '#6b7280', opacity: 1 } } }}
              />
              {isRecognizing && (
                <div className="chatgpt-input-indicator" aria-live="polite">Listening</div>
              )}
              {textInputValue.trim() && (
                <button
                  onClick={() => {
                    openAIAssistant(textInputValue);
                  }}
                  className="legacy-send-btn"
                  aria-label="Send message"
                >
                  <IoIosSend className="text-2xl text-white" />
                </button>
              )}
            </div>
            <button
              onClick={() => openAIAssistant()}
              disabled={["Sending", "Speaking"].includes(conversationStatus)}
              className={`chatgpt-voice-btn ${recording || isSpeaking || isRecognizing ? 'chatgpt-voice-btn-active' : ''}`}
              aria-pressed={recording || isSpeaking || isRecognizing}
            >
              {recording || isSpeaking || isRecognizing ? (
                <FaStop className="text-base" />
              ) : (
                <RiMicAiFill className="text-base" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

LexChatbot.displayName = 'LexChatbot';

LexChatbot.propTypes = {
  onToggle: PropTypes.func,
  userData: PropTypes.object,
  onSpeakingChange: PropTypes.func,
  autoFocusInput: PropTypes.bool,
};

export default LexChatbot;
